import { create } from 'zustand';

/**
 * ModuleEditorState defines the shape of the module editor store.
 */
export interface ModuleEditorState {
  // Base state (Deprecated: Selection now handled by router)
  selectedModuleId: string | null;
  selectedModuleVersion: string | null;
  selectedTaskId: string | null;

  // Actions
  setSelectedModuleId: (moduleId: string | null) => void;
  setSelectedModuleVersion: (version: string | null) => void;
  setSelectedTaskId: (taskId: string | null) => void;

  /** Combined setter for module ID and version */
  selectModule: (moduleId: string, version?: string) => void;
}

export const useModuleEditorStore = create<ModuleEditorState>((set) => ({
  // Base state
  selectedModuleId: null,
  selectedModuleVersion: null,
  selectedTaskId: null,

  // Setters
  setSelectedModuleId: (moduleId) => set({ selectedModuleId: moduleId }),
  setSelectedModuleVersion: (version) => set({ selectedModuleVersion: version }),
  setSelectedTaskId: (taskId) => set({ selectedTaskId: taskId }),

  // Combined operations
  selectModule: (moduleId, version = '0.0') => set({
    selectedModuleId: moduleId,
    selectedModuleVersion: version,
    selectedTaskId: null, // Reset task selection when changing module
  }),
}));

/**
 * Hook for accessing basic module selection state
 */
export const useModuleSelection = () => {
  const moduleId = useModuleEditorStore((state) => state.selectedModuleId);
  const version = useModuleEditorStore((state) => state.selectedModuleVersion);
  const selectModule = useModuleEditorStore((state) => state.selectModule);
  const setVersion = useModuleEditorStore((state) => state.setSelectedModuleVersion);

  return {
    moduleId,
    version,
    selectModule,
    setVersion,
    hasModule: moduleId !== null,
  };
};

/**
 * Hook for accessing task selection state
 */
export const useTaskSelection = () => {
  const taskId = useModuleEditorStore((state) => state.selectedTaskId);
  const version = useModuleEditorStore((state) => state.selectedModuleVersion);
  const moduleId = useModuleEditorStore((state) => state.selectedModuleId);
  const setTaskId = useModuleEditorStore((state) => state.setSelectedTaskId);

  return {
    taskId,
    version,
    moduleId,
    setTaskId,
    hasTask: taskId !== null,
    clearTask: () => setTaskId(null),
  };
};

/**
 * Hook that provides the complete editor state
 */
export const useEditorState = () => {
  const moduleId = useModuleEditorStore((state) => state.selectedModuleId);
  const version = useModuleEditorStore((state) => state.selectedModuleVersion);
  const taskId = useModuleEditorStore((state) => state.selectedTaskId);
  const selectModule = useModuleEditorStore((state) => state.selectModule);
  const setVersion = useModuleEditorStore((state) => state.setSelectedModuleVersion);
  const setTaskId = useModuleEditorStore((state) => state.setSelectedTaskId);

  return {
    moduleId,
    version,
    taskId,
    selectModule,
    setVersion,
    setTaskId,
    hasModule: moduleId !== null,
    hasTask: taskId !== null,
  };
};

export default useModuleEditorStore;
